import { DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  HStack,
  VStack,
  Text,
  BoxProps,
  MenuList,
  Menu,
  MenuButton,
  MenuItem,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { IntegerField } from "../fields/IntegerField/IntegerField";
import { TextField } from "../fields/TextField/TextField";
import { Button, ButtonGroup, IconButton } from "@chakra-ui/button";
import {
  EllipsisIcon,
  PlusIcon,
  ReplaceAllIcon,
  ValidateIcon,
} from "../../styles/icons";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "../Dnd/SortableItem";
import { getIn, useFormikContext } from "formik";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";

interface KeyValuePairIntegrationSectionProps {
  headerLabel: string;
  fieldName: string;
  keyValuePairs: { Key: string; Value: unknown }[] | null | undefined;
  addKeyValuePair: () => void;
  validateKeyValuePairs: () => Promise<string[]>;
  replaceAllKeyValuePairs: () => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  useNumericFieldKey?: boolean;
  useNumericFieldValue?: boolean;
  disableKeyField?: boolean;
  isNonIntegratedField?: boolean;
  isExtendedField?: boolean;
}

export const KeyValuePairIntegrationSection = ({
  headerLabel,
  fieldName,
  keyValuePairs,
  addKeyValuePair,
  validateKeyValuePairs,
  replaceAllKeyValuePairs,
  setFieldValue,
  useNumericFieldKey = false,
  useNumericFieldValue = false,
  disableKeyField = false,
  isNonIntegratedField = true,
  isExtendedField = false,
  ...props
}: KeyValuePairIntegrationSectionProps & BoxProps) => {
  const { errors } = useFormikContext();
  const [errorKeys, setErrorKeys] = useState<string[]>([]);

  const [activeId, setActiveId] = useState<{
    idx: number;
    kvp: { Key: string; Value: unknown };
  } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: any) => {
    const { active } = event;
    const customFieldIdx =
      keyValuePairs?.findIndex((df) => df.Key == active.id) ?? -1;
    if (customFieldIdx == -1) {
      return;
    }
    setActiveId({
      idx: customFieldIdx,
      kvp: keyValuePairs![customFieldIdx],
    });
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (!keyValuePairs) {
      return setActiveId(null);
    }

    if (!over || over.id === null || over.id === undefined) {
      return setActiveId(null);
    }

    if (active.id == over.id) {
      return setActiveId(null);
    }

    const oldIndex = keyValuePairs?.findIndex((item) => item.Key == active.id);
    const newIndex = keyValuePairs?.findIndex((item) => item.Key == over.id);
    setFieldValue(fieldName, arrayMove(keyValuePairs, oldIndex, newIndex));
    setActiveId(null);
  };

  return (
    <>
      <Box
        w="100%"
        p={4}
        border="1px solid"
        borderColor="cherryButtonDark.50"
        borderRadius="md"
        {...props}
      >
        <HStack w="100%">
          <Text flexGrow={2} fontSize="sm">
            <b>{headerLabel}</b> :
          </Text>
          <ButtonGroup isAttached>
            <Button
              size="xs"
              leftIcon={<PlusIcon />}
              onClick={addKeyValuePair}
              colorScheme="cherryButton"
              borderRight="1px white solid"
              isDisabled={isExtendedField}
            >
              New {headerLabel}
            </Button>

            {!isNonIntegratedField && (
              <Menu size="xs">
                <MenuButton
                  as={IconButton}
                  colorScheme="cherryButton"
                  aria-label="Options"
                  title="Options"
                  icon={<EllipsisIcon />}
                  size="xs"
                  isDisabled={isExtendedField}
                />
                <MenuList>
                  <MenuItem
                    icon={<ValidateIcon />}
                    onClick={() => validateKeyValuePairs().then(setErrorKeys)}
                    fontSize="sm"
                  >
                    Validate existing Key Values
                  </MenuItem>
                  <MenuItem
                    icon={<ReplaceAllIcon />}
                    onClick={replaceAllKeyValuePairs}
                    fontSize="sm"
                  >
                    Replace All Key Values from Integration
                  </MenuItem>
                </MenuList>
              </Menu>
            )}
          </ButtonGroup>
        </HStack>

        <Divider mb={2} mt={1} />
        <VStack w="100%">
          <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={keyValuePairs?.map((cf) => cf.Key) ?? []}
              strategy={verticalListSortingStrategy}
            >
              {(keyValuePairs?.length &&
                keyValuePairs?.map((kvp, kvpIdx) => {
                  return (
                    <HStack
                      w="100%"
                      key={kvpIdx}
                      spacing={4}
                      bgColor={
                        errorKeys.find((err) => err == kvp.Key) != null
                          ? "cherryButton.50"
                          : "unset"
                      }
                    >
                      <SortableItem id={kvp.Key} w="100%">
                        {(useNumericFieldKey && (
                          <IntegerField
                            horizontal
                            valueProps={{ flexGrow: 10 }}
                            label={`Key ${kvpIdx + 1}`}
                            name={`${fieldName}[${kvpIdx}].Key`}
                            isDisabled={disableKeyField}
                          />
                        )) || (
                          <TextField
                            horizontal
                            valueProps={{ flexGrow: 10 }}
                            label={`Key ${kvpIdx + 1}`}
                            name={`${fieldName}[${kvpIdx}].Key`}
                            isDisabled={disableKeyField}
                          />
                        )}
                        {(useNumericFieldValue && (
                          <IntegerField
                            horizontal
                            valueProps={{ flexGrow: 10 }}
                            label={`Value ${kvpIdx + 1}`}
                            name={`${fieldName}[${kvpIdx}].Value`}
                          />
                        )) || (
                          <TextField
                            horizontal
                            valueProps={{ flexGrow: 10 }}
                            label={`Value ${kvpIdx + 1}`}
                            name={`${fieldName}[${kvpIdx}].Value`}
                          />
                        )}

                        <Box h="100%" pb={1} alignSelf="flex-end">
                          <IconButton
                            aria-label={`Delete ${headerLabel}.`}
                            title={`Delete ${headerLabel}.`}
                            icon={<DeleteIcon />}
                            size="sm"
                            onClick={() => {
                              keyValuePairs?.splice(kvpIdx, 1);
                              setFieldValue(fieldName, keyValuePairs);
                            }}
                            isDisabled={isExtendedField}
                          />
                        </Box>
                      </SortableItem>
                    </HStack>
                  );
                })) || (
                <Text fontStyle="italic" fontSize="sm">
                  {headerLabel} has not been set.
                </Text>
              )}
            </SortableContext>
            <DragOverlay style={{ minWidth: "800px" }}>
              {activeId ? (
                <HStack
                  w="100%"
                  spacing={4}
                  bgColor={
                    errorKeys.find((err) => err == activeId.kvp?.Key) != null
                      ? "cherryButton.50"
                      : "unset"
                  }
                >
                  <SortableItem id={activeId!.kvp.Key} w="100%">
                    {(useNumericFieldKey && (
                      <IntegerField
                        horizontal
                        valueProps={{ flexGrow: 10 }}
                        label={`Key ${activeId.idx + 1}`}
                        name={`${fieldName}[${activeId.idx}].Key`}
                        isDisabled={disableKeyField}
                      />
                    )) || (
                      <TextField
                        horizontal
                        valueProps={{ flexGrow: 10 }}
                        label={`Key ${activeId.idx + 1}`}
                        name={`${fieldName}[${activeId.idx}].Key`}
                        isDisabled={disableKeyField}
                      />
                    )}
                    {(useNumericFieldValue && (
                      <IntegerField
                        horizontal
                        valueProps={{ flexGrow: 10 }}
                        label={`Value ${activeId.idx + 1}`}
                        name={`${fieldName}[${activeId.idx}].Value`}
                      />
                    )) || (
                      <TextField
                        horizontal
                        valueProps={{ flexGrow: 10 }}
                        label={`Value ${activeId.idx + 1}`}
                        name={`${fieldName}[${activeId.idx}].Value`}
                      />
                    )}

                    <Box h="100%" pb={1} alignSelf="flex-end">
                      <IconButton
                        aria-label={`Delete ${headerLabel}.`}
                        title={`Delete ${headerLabel}.`}
                        icon={<DeleteIcon />}
                        size="sm"
                      />
                    </Box>
                  </SortableItem>
                </HStack>
              ) : null}
            </DragOverlay>
          </DndContext>
        </VStack>
      </Box>
      {getIn(errors, fieldName) && (
        <ErrorMessage>{getIn(errors, fieldName)}</ErrorMessage>
      )}
    </>
  );
};
